import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Leaf, Lock, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ResetPassword = () => {
  const { getLocalizedText } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwords, setPasswords] = useState({
    password: '',
    confirmPassword: ''
  });

  const texts = {
    resetPassword: { fi: 'Nollaa salasana', fr: 'Réinitialiser le mot de passe', en: 'Reset Password' },
    newPassword: { fi: 'Uusi salasana', fr: 'Nouveau mot de passe', en: 'New Password' },
    confirmPassword: { fi: 'Vahvista salasana', fr: 'Confirmer le mot de passe', en: 'Confirm Password' },
    resetBtn: { fi: 'Aseta uusi salasana', fr: 'Définir nouveau mot de passe', en: 'Set New Password' },
    backToLogin: { fi: 'Takaisin kirjautumiseen', fr: 'Retour à la connexion', en: 'Back to login' },
    invalidLink: { fi: 'Virheellinen tai vanhentunut linkki', fr: 'Lien invalide ou expiré', en: 'Invalid or expired link' },
    invalidLinkDesc: { fi: 'Tämä salasanan nollauslinkki on virheellinen tai vanhentunut.', fr: 'Ce lien de réinitialisation est invalide ou a expiré.', en: 'This password reset link is invalid or has expired.' },
    requestNewLink: { fi: 'Pyydä uusi linkki', fr: 'Demander un nouveau lien', en: 'Request new link' },
    verifying: { fi: 'Tarkistetaan linkkiä...', fr: 'Vérification du lien...', en: 'Verifying link...' },
    passwordsNoMatch: { fi: 'Salasanat eivät täsmää', fr: 'Les mots de passe ne correspondent pas', en: 'Passwords do not match' },
    passwordTooShort: { fi: 'Salasanan pitää olla vähintään 6 merkkiä', fr: 'Le mot de passe doit contenir au moins 6 caractères', en: 'Password must be at least 6 characters' },
    successTitle: { fi: 'Salasana nollattu!', fr: 'Mot de passe réinitialisé!', en: 'Password Reset!' },
    successDesc: { fi: 'Voit nyt kirjautua uudella salasanallasi.', fr: 'Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.', en: 'You can now log in with your new password.' },
    goToLogin: { fi: 'Siirry kirjautumiseen', fr: 'Aller à la connexion', en: 'Go to Login' },
    enterNewPassword: { fi: 'Syötä uusi salasanasi', fr: 'Entrez votre nouveau mot de passe', en: 'Enter your new password' }
  };

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setTokenValid(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/auth/verify-reset-token/${token}`);
        setTokenValid(true);
        setEmail(response.data.email);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (passwords.password !== passwords.confirmPassword) {
      toast.error(getLocalizedText(texts.passwordsNoMatch));
      return;
    }

    if (passwords.password.length < 6) {
      toast.error(getLocalizedText(texts.passwordTooShort));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token: token,
        new_password: passwords.password
      });
      setSuccess(true);
    } catch (error) {
      const message = error.response?.data?.detail || 'An error occurred';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4">
        <div className="text-center">
          <Leaf className="h-12 w-12 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-foreground/60">{getLocalizedText(texts.verifying)}</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4" data-testid="invalid-token-page">
        <div className="max-w-md w-full space-y-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Leaf className="h-10 w-10 text-primary" />
            <span className="text-3xl font-bold text-primary font-serif">love4vegan</span>
          </div>
          
          <div className="bg-destructive/10 p-8 rounded-2xl">
            <XCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">{getLocalizedText(texts.invalidLink)}</h1>
            <p className="text-foreground/60 mb-6">{getLocalizedText(texts.invalidLinkDesc)}</p>
            <Link to="/auth">
              <Button className="bg-primary hover:bg-primary/90">
                {getLocalizedText(texts.requestNewLink)}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4" data-testid="reset-success-page">
        <div className="max-w-md w-full space-y-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Leaf className="h-10 w-10 text-primary" />
            <span className="text-3xl font-bold text-primary font-serif">love4vegan</span>
          </div>
          
          <div className="bg-primary/10 p-8 rounded-2xl">
            <CheckCircle className="h-16 w-16 text-primary mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">{getLocalizedText(texts.successTitle)}</h1>
            <p className="text-foreground/60 mb-6">{getLocalizedText(texts.successDesc)}</p>
            <Link to="/auth">
              <Button className="bg-primary hover:bg-primary/90">
                {getLocalizedText(texts.goToLogin)}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4" data-testid="reset-password-page">
      <div className="max-w-md w-full space-y-8">
        <Link to="/auth" className="inline-flex items-center text-foreground/60 hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {getLocalizedText(texts.backToLogin)}
        </Link>

        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Leaf className="h-10 w-10 text-primary" />
            <span className="text-3xl font-bold text-primary font-serif">love4vegan</span>
          </div>
          <h1 className="text-3xl font-bold">{getLocalizedText(texts.resetPassword)}</h1>
          <p className="text-foreground/60 mt-2">{getLocalizedText(texts.enterNewPassword)}</p>
          {email && <p className="text-sm text-primary mt-1">{email}</p>}
        </div>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6 bg-card p-8 rounded-2xl border border-border">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                {getLocalizedText(texts.newPassword)}
              </Label>
              <Input
                id="password"
                type="password"
                value={passwords.password}
                onChange={(e) => setPasswords({ ...passwords, password: e.target.value })}
                placeholder="••••••••"
                required
                minLength={6}
                className="h-12"
                data-testid="new-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                {getLocalizedText(texts.confirmPassword)}
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwords.confirmPassword}
                onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                placeholder="••••••••"
                required
                minLength={6}
                className="h-12"
                data-testid="confirm-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-primary hover:bg-primary/90 rounded-full text-lg"
            data-testid="reset-submit-btn"
          >
            {loading ? '...' : getLocalizedText(texts.resetBtn)}
          </Button>
        </form>
      </div>
    </div>
  );
};
